import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, CreditCard, Loader2, MapPin, Package, Search, ShoppingBag, Truck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, resolveMediaUrl } from "@/lib/api";
import Seo from "@/components/Seo";

const money = (value, currency = "USD") => {
  try { return new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format(Number(value || 0)); }
  catch { return `${Number(value || 0).toFixed(2)} ${currency}`; }
};

const inputCls = "h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-[16px] outline-none focus:border-brand-orange";

function ProductImage({ product, className = "" }) {
  const src = resolveMediaUrl(product?.image_url);
  return src
    ? <img src={src} alt={product?.name || "Produit"} className={className} />
    : <div className={`${className} grid place-items-center bg-slate-100`}><Package className="h-10 w-10 text-slate-300" /></div>;
}

export default function MarketplaceHome() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/marketplace/products")
      .then(({ data }) => setProducts(data || []))
      .catch(() => toast.error("Impossible de charger la Marketplace"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return products;
    return products.filter((p) => `${p.name} ${p.category} ${p.brand || ""}`.toLowerCase().includes(needle));
  }, [products, q]);

  const categories = [...new Set(products.map((p) => p.category).filter(Boolean))].slice(0, 8);

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "TeamWork Marketplace",
    url: "https://teamwork-africa.com/marketplace",
    description: "Marketplace Team Work Africa pour découvrir et acheter des produits avec paiement en ligne ou à la livraison selon disponibilité.",
    isPartOf: { "@id": "https://teamwork-africa.com/#website" },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: products.slice(0, 50).map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: product.name,
        url: `https://teamwork-africa.com/marketplace/${product.slug}`,
      })),
    },
  };

  return <>
    <Seo
      title="Marketplace | Acheter en ligne avec Team Work Africa"
      description="Découvrez la marketplace Team Work Africa : téléphones, mode, tech, accessoires et autres produits avec paiement en ligne ou à la livraison selon disponibilité."
      path="/marketplace"
      schema={schema}
    />
    <div className="min-h-screen bg-slate-50">
    <section className="bg-brand-navy text-white">
      <div className="container-app py-12 sm:py-16">
        <div className="text-xs font-black uppercase tracking-[.2em] text-brand-orange">TeamWork Marketplace</div>
        <h1 className="mt-3 max-w-3xl text-3xl font-black sm:text-5xl">Achetez directement avec TeamWork.</h1>
        <p className="mt-4 max-w-2xl text-sm leading-6 text-white/70 sm:text-base">Téléphones, baskets, accessoires et autres marchandises publiés et contrôlés par TeamWork. Paiement en ligne ou à la livraison selon votre choix.</p>
        <div className="relative mt-7 max-w-2xl">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Rechercher un produit..." className="h-14 w-full rounded-2xl bg-white pl-12 pr-4 text-[16px] font-semibold text-slate-900 outline-none" />
        </div>
      </div>
    </section>

    <div className="container-app py-8">
      {!!categories.length && <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
        {categories.map((category)=><button key={category} onClick={()=>setQ(category)} className="whitespace-nowrap rounded-full bg-white px-4 py-2 text-xs font-black text-brand-navy ring-1 ring-slate-200">{category}</button>)}
        {q && <button onClick={()=>setQ("")} className="whitespace-nowrap rounded-full bg-brand-orange px-4 py-2 text-xs font-black text-white">Tout voir</button>}
      </div>}

      {loading ? <div className="grid min-h-64 place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand-orange" /></div>
      : !filtered.length ? <div className="rounded-3xl bg-white p-10 text-center text-sm text-slate-500 ring-1 ring-slate-200">Aucun produit disponible pour le moment.</div>
      : <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {filtered.map((product)=><Link key={product.id} to={`/marketplace/${product.slug}`} className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-1 hover:shadow-md">
          <ProductImage product={product} className="aspect-square w-full object-cover" />
          <div className="p-3 sm:p-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-brand-orange">{product.category}</div>
            <h2 className="mt-1 line-clamp-2 text-sm font-black text-brand-navy sm:text-base">{product.name}</h2>
            <div className="mt-2 flex items-end justify-between gap-2">
              <div className="text-sm font-black text-slate-950 sm:text-lg">{money(product.price, product.currency)}</div>
              {product.sold_out && <span className="rounded-full bg-slate-100 px-2 py-1 text-[9px] font-black text-slate-500">ÉPUISÉ</span>}
            </div>
          </div>
        </Link>)}
      </div>}
    </div>
  </div>
  </>;
}

export function MarketplaceProduct() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    quantity: 1, full_name: "", phone: "", whatsapp: "", email: "",
    city: "Kinshasa", commune: "", address: "", landmark: "", delivery_instructions: "",
    delivery_method: "delivery", payment_method: "online",
  });

  useEffect(() => {
    api.get(`/marketplace/products/${slug}`)
      .then(({ data }) => setProduct(data))
      .catch(() => toast.error("Produit introuvable"))
      .finally(() => setLoading(false));
  }, [slug]);

  const set = (k,v) => setForm((x)=>({...x,[k]:v}));
  const total = product ? Number(product.price || 0) * Number(form.quantity || 1) + (form.delivery_method === "delivery" ? Number(product.delivery_fee || 0) : 0) : 0;

  const order = async () => {
    if (!form.full_name.trim() || !form.phone.trim() || !form.city.trim() || !form.address.trim()) {
      toast.error("Nom, téléphone, ville et adresse sont obligatoires.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/marketplace/orders", {
        ...form,
        product_id: product.id,
        quantity: Number(form.quantity || 1),
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      navigate(`/marketplace/commande/${data.reference}?token=${encodeURIComponent(data.verification_token)}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Commande impossible");
    } finally { setSending(false); }
  };

  if (loading) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand-orange"/></div>;
  if (!product) return <div className="container-app py-16">Produit introuvable.</div>;

  const productUrl = `https://teamwork-africa.com/marketplace/${product.slug}`;
  const productImage = resolveMediaUrl(product.image_url);
  const productDescription = (product.description || `${product.name} disponible sur TeamWork Marketplace.`).slice(0, 180);
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: productDescription,
    image: productImage ? [productImage] : undefined,
    sku: product.sku || undefined,
    brand: product.brand ? { "@type": "Brand", name: product.brand } : undefined,
    category: product.category || undefined,
    url: productUrl,
    offers: {
      "@type": "Offer",
      url: productUrl,
      price: Number(product.price || 0),
      priceCurrency: product.currency || "USD",
      availability: product.sold_out || Number(product.available || 0) <= 0
        ? "https://schema.org/OutOfStock"
        : "https://schema.org/InStock",
    },
  };

  return <>
    <Seo
      title={`${product.name} | TeamWork Marketplace`}
      description={productDescription}
      path={`/marketplace/${product.slug}`}
      image={productImage || undefined}
      imageAlt={product.name}
      type="product"
      schema={productSchema}
    />
    <div className="min-h-screen bg-slate-50 py-6 sm:py-10">
    <div className="container-app">
      <Link to="/marketplace" className="inline-flex items-center gap-2 text-sm font-black text-brand-navy"><ArrowLeft className="h-4 w-4"/>Marketplace</Link>
      <div className="mt-5 grid gap-6 lg:grid-cols-[1fr_1fr]">
        <div className="overflow-hidden rounded-3xl bg-white ring-1 ring-slate-200">
          <ProductImage product={product} className="aspect-square w-full object-cover" />
        </div>
        <div>
          <div className="text-xs font-black uppercase tracking-[.16em] text-brand-orange">{product.category}</div>
          <h1 className="mt-2 text-3xl font-black text-brand-navy">{product.name}</h1>
          {product.brand && <div className="mt-1 text-sm text-slate-500">{product.brand}</div>}
          <div className="mt-4 flex items-baseline gap-3">
            <span className="text-3xl font-black text-slate-950">{money(product.price, product.currency)}</span>
            {product.compare_at_price > product.price && <span className="text-sm text-slate-400 line-through">{money(product.compare_at_price, product.currency)}</span>}
          </div>
          <p className="mt-5 whitespace-pre-line text-sm leading-7 text-slate-600">{product.description || "Produit disponible chez TeamWork."}</p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold">
            <span className="rounded-full bg-white px-3 py-2 ring-1 ring-slate-200">{product.available} disponible(s)</span>
            {Number(product.delivery_fee || 0) > 0 && <span className="rounded-full bg-white px-3 py-2 ring-1 ring-slate-200">Livraison {money(product.delivery_fee, product.currency)}</span>}
          </div>

          <div className="mt-7 rounded-3xl bg-white p-5 ring-1 ring-slate-200 sm:p-6">
            <div className="flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-brand-orange"/><h2 className="font-black text-brand-navy">Commander</h2></div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <label><span className="mb-1 block text-xs font-bold">Nom complet *</span><input className={inputCls} value={form.full_name} onChange={(e)=>set("full_name",e.target.value)}/></label>
              <label><span className="mb-1 block text-xs font-bold">Téléphone *</span><input className={inputCls} value={form.phone} onChange={(e)=>set("phone",e.target.value)}/></label>
              <label><span className="mb-1 block text-xs font-bold">WhatsApp</span><input className={inputCls} value={form.whatsapp} onChange={(e)=>set("whatsapp",e.target.value)}/></label>
              <label><span className="mb-1 block text-xs font-bold">Email</span><input type="email" className={inputCls} value={form.email} onChange={(e)=>set("email",e.target.value)}/></label>
              <label><span className="mb-1 block text-xs font-bold">Ville *</span><input className={inputCls} value={form.city} onChange={(e)=>set("city",e.target.value)}/></label>
              <label><span className="mb-1 block text-xs font-bold">Commune / quartier</span><input className={inputCls} value={form.commune} onChange={(e)=>set("commune",e.target.value)}/></label>
              <label className="sm:col-span-2"><span className="mb-1 block text-xs font-bold">Adresse de livraison *</span><input className={inputCls} value={form.address} onChange={(e)=>set("address",e.target.value)}/></label>
              <label className="sm:col-span-2"><span className="mb-1 block text-xs font-bold">Repère</span><input className={inputCls} value={form.landmark} onChange={(e)=>set("landmark",e.target.value)}/></label>
              <label><span className="mb-1 block text-xs font-bold">Quantité</span><input type="number" min="1" max={Math.max(1, product.available)} className={inputCls} value={form.quantity} onChange={(e)=>set("quantity",e.target.value)}/></label>
              <label><span className="mb-1 block text-xs font-bold">Livraison</span><select className={inputCls} value={form.delivery_method} onChange={(e)=>set("delivery_method",e.target.value)}><option value="delivery">Livraison à domicile</option><option value="pickup">Retrait</option></select></label>
              <label className="sm:col-span-2"><span className="mb-1 block text-xs font-bold">Instructions de livraison</span><textarea rows={3} className="w-full rounded-xl border border-slate-200 p-3 text-[16px] outline-none focus:border-brand-orange" value={form.delivery_instructions} onChange={(e)=>set("delivery_instructions",e.target.value)}/></label>
            </div>

            <div className="mt-5 text-sm font-black text-brand-navy">Mode de paiement</div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={()=>set("payment_method","online")} className={`rounded-2xl border p-4 text-left ${form.payment_method==="online"?"border-brand-orange bg-orange-50":"border-slate-200"}`}><CreditCard className="h-5 w-5 text-brand-orange"/><div className="mt-2 text-sm font-black">Paiement en ligne</div><div className="mt-1 text-xs text-slate-500">Carte ou Mobile Money via notre paiement sécurisé.</div></button>
              <button type="button" onClick={()=>set("payment_method","cod")} className={`rounded-2xl border p-4 text-left ${form.payment_method==="cod"?"border-brand-orange bg-orange-50":"border-slate-200"}`}><Truck className="h-5 w-5 text-brand-orange"/><div className="mt-2 text-sm font-black">Espèces à la livraison</div><div className="mt-1 text-xs text-slate-500">TeamWork confirme la commande avant livraison.</div></button>
            </div>

            <div className="mt-5 flex items-center justify-between rounded-2xl bg-slate-50 p-4"><span className="text-sm font-bold text-slate-600">Total</span><span className="text-xl font-black text-brand-navy">{money(total, product.currency)}</span></div>
            <button disabled={sending || product.sold_out} onClick={order} className="mt-4 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-orange px-5 text-sm font-black text-white disabled:opacity-50">{sending?<Loader2 className="h-4 w-4 animate-spin"/>:<ShoppingBag className="h-4 w-4"/>}{product.sold_out?"Produit épuisé":sending?"Validation…":"Confirmer la commande"}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
  </>;
}

export function MarketplaceOrder() {
  const { reference } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timer;
    const load = () => api.get(`/marketplace/orders/${reference}`, { params: { token } })
      .then(({data}) => setOrder(data))
      .catch(()=>{})
      .finally(()=>setLoading(false));
    load();
    timer = window.setInterval(load, 5000);
    return () => window.clearInterval(timer);
  }, [reference, token]);

  if (loading) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand-orange"/></div>;
  if (!order) return <div className="container-app py-16">Commande introuvable.</div>;

  const paid = ["paid","paid_on_delivery"].includes(order.payment_status);
  return <div className="min-h-screen bg-slate-50 py-10">
    <div className="mx-auto max-w-2xl px-4">
      <div className="rounded-3xl bg-white p-6 ring-1 ring-slate-200 sm:p-8">
        <div className={`grid h-14 w-14 place-items-center rounded-2xl ${paid?"bg-emerald-50 text-emerald-600":"bg-orange-50 text-brand-orange"}`}>{paid?<CheckCircle2 className="h-7 w-7"/>:<Package className="h-7 w-7"/>}</div>
        <div className="mt-5 text-xs font-black uppercase tracking-[.15em] text-brand-orange">Commande {order.reference}</div>
        <h1 className="mt-2 text-2xl font-black text-brand-navy">{order.payment_method==="cod" ? "Commande reçue" : paid ? "Paiement confirmé" : "Paiement en cours de confirmation"}</h1>
        <p className="mt-2 text-sm leading-6 text-slate-500">{order.payment_method==="cod" ? "TeamWork va confirmer votre commande avant préparation et livraison." : "Cette page se met à jour automatiquement après confirmation du paiement."}</p>

        <div className="mt-6 space-y-3 rounded-2xl bg-slate-50 p-4 text-sm">
          <div className="flex justify-between gap-4"><span className="text-slate-500">Produit</span><strong className="text-right">{order.product_name} × {order.quantity}</strong></div>
          <div className="flex justify-between gap-4"><span className="text-slate-500">Paiement</span><strong>{order.payment_method==="cod"?"À la livraison":"En ligne"}</strong></div>
          <div className="flex justify-between gap-4"><span className="text-slate-500">Statut</span><strong>{order.status}</strong></div>
          <div className="flex justify-between gap-4"><span className="text-slate-500">Total</span><strong>{money(order.total,order.currency)}</strong></div>
          <div className="border-t border-slate-200 pt-3"><div className="flex items-start gap-2 text-slate-600"><MapPin className="mt-0.5 h-4 w-4 shrink-0"/><span>{order.address}, {order.commune ? `${order.commune}, ` : ""}{order.city}</span></div></div>
        </div>
        <Link to="/marketplace" className="mt-6 inline-flex h-11 items-center justify-center rounded-xl bg-brand-navy px-5 text-sm font-black text-white">Retour à la Marketplace</Link>
      </div>
    </div>
  </div>;
}
